import { useEffect, useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, ExternalLink, Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useT, useLang } from "@/context/LanguageContext";

const toKey = (d) => d.toISOString().slice(0, 10);
const fromKey = (s) => (s ? new Date(`${s}T00:00:00Z`) : null);
const addDays = (d, n) => { const x = new Date(d); x.setUTCDate(x.getUTCDate() + n); return x; };

export function BookingWidget({ listing }) {
  const t = useT().booking;
  const { lang } = useLang();
  const [ci, setCi] = useState("");
  const [co, setCo] = useState("");
  const [guests, setGuests] = useState(2);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cal, setCal] = useState(null);
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => { const d = new Date(); return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)); });

  const today = toKey(new Date());
  const price = listing.price_per_night || 149;

  useEffect(() => {
    const to = toKey(addDays(new Date(), 370));
    api.get(`/listings/${listing.slug}/calendar`, { params: { from: today, to } })
      .then((r) => setCal(r.data))
      .catch(() => setCal({ synced: false }));
  }, [listing.slug, today]);

  const byDate = useMemo(() => new Map((cal?.days || []).map((d) => [d.date, d])), [cal]);

  // Prix le plus bas parmi les nuits disponibles des 31 prochains jours
  const fromPrice = useMemo(() => {
    if (!cal?.synced) return price;
    const prices = [];
    for (let d = fromKey(today), i = 0; i < 31; i++, d.setUTCDate(d.getUTCDate() + 1)) {
      const day = byDate.get(toKey(d));
      if (day?.available && day.price) prices.push(day.price);
    }
    return prices.length ? Math.min(...prices) : price;
  }, [cal, byDate, price, today]);

  const nights = useMemo(() => {
    if (!ci || !co) return 0;
    const n = Math.round((fromKey(co) - fromKey(ci)) / 86400000);
    return n > 0 ? n : 0;
  }, [ci, co]);

  const selection = useMemo(() => {
    if (!nights) return { ok: false, total: 0 };
    if (!cal || !cal.synced) return { ok: true, total: nights * price };
    const dayIn = byDate.get(ci);
    if (!dayIn || !dayIn.available || dayIn.no_checkin) return { ok: false, total: 0, err: t.unavailable };
    const dayOut = byDate.get(co);
    if (dayOut && dayOut.no_checkout) return { ok: false, total: 0, err: t.unavailable };
    let total = 0;
    for (let d = fromKey(ci); d < fromKey(co); d.setUTCDate(d.getUTCDate() + 1)) {
      const day = byDate.get(toKey(d));
      if (!day || !day.available) return { ok: false, total: 0, err: t.unavailable };
      total += day.price || 0;
    }
    if (nights < (cal.min_nights || 1)) return { ok: false, total: 0, err: `${t.minNights} : ${cal.min_nights} ${t.nights}` };
    return { ok: true, total: Math.round(total * 100) / 100 };
  }, [nights, cal, byDate, ci, co, price, t]);

  const pick = (key) => {
    if (!ci || (ci && co) || key <= ci) {
      setCi(key); setCo("");
    } else {
      setCo(key); setOpen(false);
    }
  };

  const isBlocked = (key) => {
    if (key < today) return true;
    const day = byDate.get(key);
    if (cal?.synced) {
      if (!day) return true;
      if (!day.available) return true;
      if (!ci || (ci && co)) return !!day.no_checkin;
      if (key > ci) {
        if (day.no_checkout) return true;
        for (let d = fromKey(ci); d < fromKey(key); d.setUTCDate(d.getUTCDate() + 1)) {
          const dd = byDate.get(toKey(d));
          if (!dd || !dd.available) return true;
        }
      }
    }
    return false;
  };

  const monthDays = useMemo(() => {
    const y = viewMonth.getUTCFullYear(), m = viewMonth.getUTCMonth();
    const first = new Date(Date.UTC(y, m, 1));
    const count = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    const lead = (first.getUTCDay() + 6) % 7;
    const days = [];
    for (let i = 0; i < lead; i++) days.push(null);
    for (let d = 1; d <= count; d++) days.push(toKey(new Date(Date.UTC(y, m, d))));
    return days;
  }, [viewMonth]);

  const monthLabel = useMemo(
    () => new Intl.DateTimeFormat(lang === "fr" ? "fr-FR" : "en-GB", { month: "long", year: "numeric", timeZone: "UTC" }).format(viewMonth),
    [viewMonth, lang]
  );
  const weekdays = useMemo(() => {
    const base = new Date(Date.UTC(2026, 0, 5)); // un lundi
    return Array.from({ length: 7 }, (_, i) =>
      new Intl.DateTimeFormat(lang === "fr" ? "fr-FR" : "en-GB", { weekday: "narrow", timeZone: "UTC" }).format(addDays(base, i)));
  }, [lang]);

  const fmt = (key) => key ? new Intl.DateTimeFormat(lang === "fr" ? "fr-FR" : "en-GB", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" }).format(fromKey(key)) : "";
  const shiftMonth = (n) => setViewMonth((v) => new Date(Date.UTC(v.getUTCFullYear(), v.getUTCMonth() + n, 1)));

  const book = async () => {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/bookings/checkout", {
        slug: listing.slug, check_in: ci, check_out: co, guests,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const F = "w-full bg-ivory border border-ink/20 px-3 py-2.5 text-sm focus:border-gold focus:outline-none transition-colors";

  return (
    <div className="border border-ink/12 p-6" data-testid="booking-widget">
      <div className="mb-5">
        <span className="overline text-ink/50 block mb-1">{t.from}</span>
        <span className="font-serif text-3xl" data-testid="booking-from-price">{fromPrice} €</span>
        <span className="text-ink/50 text-sm"> {t.perNight}</span>
      </div>
      <p className="overline text-ink/50 mb-4">{t.title}</p>

      <div className="relative mb-3">
        <div className="grid grid-cols-2 gap-3">
          <button type="button" onClick={() => setOpen(true)} data-testid="booking-checkin"
            className={`${F} text-left ${ci ? "text-ink" : "text-ink/40"}`}>
            <span className="block text-xs text-ink/60 mb-0.5">{t.checkIn}</span>{fmt(ci) || "—"}
          </button>
          <button type="button" onClick={() => setOpen(true)} data-testid="booking-checkout"
            className={`${F} text-left ${co ? "text-ink" : "text-ink/40"}`}>
            <span className="block text-xs text-ink/60 mb-0.5">{t.checkOut}</span>{fmt(co) || "—"}
          </button>
        </div>
        {open && (
          <div className="absolute z-30 left-1/2 -translate-x-1/2 mt-2 bg-ivory border border-ink/15 p-4 w-[320px] max-w-[92vw]" data-testid="booking-calendar">
            <div className="flex items-center justify-between mb-3">
              <button type="button" onClick={() => shiftMonth(-1)} className="p-1 hover:text-gold transition-colors" data-testid="cal-prev"><ChevronLeft size={16} /></button>
              <span className="text-sm capitalize">{monthLabel}</span>
              <button type="button" onClick={() => shiftMonth(1)} className="p-1 hover:text-gold transition-colors" data-testid="cal-next"><ChevronRight size={16} /></button>
            </div>
            <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] text-ink/40 mb-1">
              {weekdays.map((w, i) => <span key={i}>{w}</span>)}
            </div>
            <div className="grid grid-cols-7 gap-0.5">
              {monthDays.map((key, i) => {
                if (!key) return <span key={i} />;
                const blocked = isBlocked(key);
                const selected = key === ci || key === co;
                const inRange = ci && co && key > ci && key < co;
                const dayData = byDate.get(key);
                const showPrice = cal?.synced && dayData?.available && !blocked && key >= today;
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={blocked}
                    onClick={() => pick(key)}
                    data-testid={`cal-day-${key}`}
                    className={`h-10 flex flex-col items-center justify-center leading-none transition-colors ${
                      selected ? "bg-ink text-ivory" :
                      inRange ? "bg-sand/60" :
                      blocked ? "text-ink/25 line-through cursor-not-allowed" :
                      "hover:bg-sand/50"
                    }`}
                  >
                    <span className="text-xs">{parseInt(key.slice(8, 10))}</span>
                    {showPrice && <span className={`text-[8px] mt-0.5 ${selected ? "text-ivory/70" : "text-ink/40"}`}>{dayData.price}€</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <label className="text-xs text-ink/60 block mb-5">{t.guests}
        <select value={guests} onChange={(e) => setGuests(parseInt(e.target.value))} className={F} data-testid="booking-guests">
          {[1, 2, 3, 4, 5, 6].map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
      </label>

      {nights > 0 && selection.ok && (
        <div className="flex justify-between text-sm mb-4 pb-4 border-b border-ink/10" data-testid="booking-total">
          <span className="text-ink/60">{t.total} · {nights} {nights > 1 ? t.nights : t.night}</span>
          <span className="font-serif text-2xl">{selection.total} €</span>
        </div>
      )}
      {nights > 0 && selection.err && (
        <p className="text-destructive text-sm mb-4" data-testid="booking-unavailable">{selection.err}</p>
      )}
      {error && <p className="text-destructive text-sm mb-3" data-testid="booking-error">{error}</p>}

      <button onClick={book} disabled={loading || !selection.ok} data-testid="booking-cta"
        className="w-full bg-ink text-ivory py-4 overline hover:bg-gold hover:text-ink transition-colors disabled:opacity-40">
        {loading ? t.processing : nights < 1 ? t.selectDates : selection.ok ? `${t.cta} · ${selection.total} €` : t.unavailable}
      </button>
      <p className="flex items-center justify-center gap-1.5 text-xs text-ink/40 mt-3"><Lock size={12} /> {t.secure}</p>
      {cal?.synced && <p className="text-center text-xs text-ink/40 mt-2" data-testid="booking-synced-note">{t.syncedNote}</p>}
      {listing.booking_url && listing.booking_url !== "#" && (
        <a href={listing.booking_url} target="_blank" rel="noreferrer" data-testid="airbnb-link"
          className="mt-4 flex items-center justify-center gap-1.5 text-sm text-ink/60 hover:text-gold transition-colors">
          {t.airbnb} <ExternalLink size={14} />
        </a>
      )}
    </div>
  );
}
